import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CartItem, Order, PrintSize } from '../types/cart';

interface CartContextType {
  items: CartItem[];
  totalItems: number;
  totalAmount: number;
  orders: Order[];
  addToCart: (imageId: string, imageSrc: string, imageTitle: string, size: PrintSize, quantity: number) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  updateSize: (itemId: string, size: PrintSize) => void;
  clearCart: () => void;
  getItemCount: () => number;
  getTotalPrice: () => number;
  submitOrder: (customerInfo: Order['customerInfo']) => Promise<Order>;
  getOrders: () => Order[];
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('tamerintech-cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        setItems(parsedCart.map((item: CartItem) => ({
          ...item,
          addedAt: new Date(item.addedAt)
        })));
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    }

    const savedOrders = localStorage.getItem('tamerintech-orders');
    if (savedOrders) {
      try {
        const parsedOrders = JSON.parse(savedOrders);
        setOrders(parsedOrders.map((order: Order) => ({
          ...order,
          createdAt: new Date(order.createdAt),
          updatedAt: new Date(order.updatedAt)
        })));
      } catch (error) {
        console.error('Error loading orders from localStorage:', error);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('tamerintech-cart', JSON.stringify(items));
  }, [items]);

  // Save orders to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('tamerintech-orders', JSON.stringify(orders));
  }, [orders]);

  const addToCart = (
    imageId: string,
    imageSrc: string,
    imageTitle: string,
    size: PrintSize,
    quantity: number
  ) => {
    const newItem: CartItem = {
      id: `${imageId}-${size.id}-${Date.now()}`,
      imageId,
      imageSrc,
      imageTitle,
      size,
      quantity,
      price: size.price * quantity,
      addedAt: new Date()
    };

    setItems(prevItems => [...prevItems, newItem]);
  };

  const removeFromCart = (itemId: string) => {
    setItems(prevItems => prevItems.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }

    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? { ...item, quantity, price: item.size.price * quantity }
          : item
      )
    );
  };

  const updateSize = (itemId: string, size: PrintSize) => {
    setItems(prevItems =>
      prevItems.map(item =>
        item.id === itemId
          ? { ...item, size, price: size.price * item.quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  const getItemCount = () => {
    return items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return items.reduce((total, item) => total + item.price, 0);
  };

  const submitOrder = async (customerInfo: Order['customerInfo']): Promise<Order> => {
    const order: Order = {
      id: `order-${Date.now()}`,
      items: [...items],
      totalAmount: getTotalPrice(),
      customerInfo,
      status: 'pending',
      createdAt: new Date(),
      updatedAt: new Date()
    };

    try {
      // Send order to API
      const response = await fetch('https://simplechat-kids-backend.azurewebsites.net/api/saveAIOrder', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          orderId: order.id,
          items: order.items.map(item => ({
            imageId: item.imageId,
            imageTitle: item.imageTitle,
            imageSrc: item.imageSrc,
            size: item.size.name,
            dimensions: item.size.dimensions,
            printType: item.size.printType,
            quantity: item.quantity,
            unitPrice: item.size.price,
            totalPrice: item.price
          })),
          totalAmount: order.totalAmount,
          customer: {
            name: customerInfo.name,
            email: customerInfo.email,
            phone: customerInfo.phone,
            address: customerInfo.address,
            notes: customerInfo.notes
          },
          orderDate: order.createdAt.toISOString(),
          status: order.status
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const apiResponse = await response.json();
      console.log('Order submitted successfully:', apiResponse);
      
      // Update order status to confirmed if API call was successful
      order.status = 'confirmed';
      order.updatedAt = new Date();
    } catch (error) {
      console.error('Error submitting order to API:', error);
      // Keep order status as 'pending' if API call failed
      // Still save locally so order is not lost
    }

    // Save order to local storage (regardless of API success/failure)
    setOrders(prevOrders => [...prevOrders, order]);
    
    // Clear cart after order creation
    clearCart();

    return order;
  };

  const getOrders = () => {
    return orders;
  };

  const totalItems = getItemCount();
  const totalAmount = getTotalPrice();

  return (
    <CartContext.Provider
      value={{
        items,
        totalItems,
        totalAmount,
        orders,
        addToCart,
        removeFromCart,
        updateQuantity,
        updateSize,
        clearCart,
        getItemCount,
        getTotalPrice,
        submitOrder,
        getOrders
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;